import styled, { css } from 'styled-components'

export const FavoritesShell = styled.div`
  width: 100%;
  padding: 36px clamp(18px, 3vw, 36px) 56px;
  margin-top: 80px;
`

export const WorkspaceFrame = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 24px;
  min-height: calc(100vh - 220px);

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

const panelSurface = css`
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, ${p => p.theme.palette.colors.shellGradientStart} 0%, transparent 42%),
    ${p => p.theme.palette.colors.surfaceElevatedSoft};
  box-shadow:
    0 20px 54px ${p => p.theme.palette.colors.shadowStrong},
    inset 0 1px 0 ${p => p.theme.palette.colors.borderSoft};
  backdrop-filter: blur(18px);
`

export const SidebarPanel = styled.aside`
  ${panelSurface};
  padding: 22px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: sticky;
  top: 100px;
  height: fit-content;

  @media (max-width: 1100px) {
    position: static;
  }
`

export const MainPanel = styled.section`
  ${panelSurface};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
`

export const SectionEyebrow = styled.div`
  font-family: Poppins;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${p => p.theme.palette.colors.subdued};
`

export const SidebarTitle = styled.div`
  font-family: Gilroy;
  font-size: clamp(24px, 2vw, 28px);
  font-weight: 700;
  line-height: 1;
  color: ${p => p.theme.palette.colors.text};
`

export const SidebarDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${p => p.theme.palette.colors.muted};
`

export const SidebarHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const FolderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const FolderRow = styled.div`
  width: 100%;
  border: 1px solid ${p => p.active ? p.theme.palette.blue.main : p.theme.palette.colors.borderSoft};
  background: ${p => p.active ? p.theme.palette.colors.badgeMuted : p.theme.palette.colors.surfaceElevated};
  color: ${p => p.theme.palette.colors.text};
  border-radius: 18px;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  box-shadow: ${p => p.dragging ? `0 24px 56px ${p.theme.palette.colors.shadowLift}` : 'none'};
  opacity: ${p => p.dimmed ? 0.78 : 1};

  &:hover {
    transform: translateY(-1px);
  }
`

export const FolderMeta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 3px;
`

export const FolderName = styled.div`
  font-family: Gilroy;
  font-size: 16px;
  font-weight: 700;
  color: ${p => p.theme.palette.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const FolderCaption = styled.div`
  font-size: 12px;
  color: ${p => p.theme.palette.colors.subdued};
`

export const FolderCount = styled.div`
  min-width: 30px;
  height: 30px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: ${p => p.theme.palette.colors.surfaceElevatedStrong};
  color: ${p => p.theme.palette.colors.text};
`

export const FolderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.palette.colors.surfaceElevatedStrong};
  color: ${p => p.theme.palette.colors.text};
  cursor: pointer;
  transition: transform 0.18s ease, background 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    background: ${p => p.theme.palette.colors.badgeMuted};
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
    transform: none;
  }
`

export const FolderCreateRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 6px;
  border-top: 1px solid ${p => p.theme.palette.colors.borderSoft};
`

export const InlineInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 14px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  background: ${p => p.theme.palette.colors.surfaceElevated};
  color: ${p => p.theme.palette.colors.text};
  padding: 0 14px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${p => p.theme.palette.blue.main};
    box-shadow: 0 0 0 4px ${p => p.theme.palette.colors.badgeMuted};
  }
`

export const InlineActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`

export const MainHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  padding-bottom: 18px;
  border-bottom: 1px solid ${p => p.theme.palette.colors.borderSoft};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

export const MainHeadingBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const MainTitle = styled.div`
  font-family: Gilroy;
  font-size: clamp(28px, 3vw, 38px);
  font-weight: 700;
  line-height: 1;
  color: ${p => p.theme.palette.colors.text};
`

export const MainSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${p => p.theme.palette.colors.muted};
`

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

export const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

export const SelectSlot = styled.div`
  min-width: 220px;
`

export const SelectionPill = styled.div`
  min-height: 42px;
  border-radius: 999px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${p => p.theme.palette.colors.badgeMuted};
  color: ${p => p.theme.palette.colors.text};
  font-size: 13px;
  font-weight: 700;
`

export const ViewSwitch = styled.div`
  display: flex;
  padding: 4px;
  border-radius: 999px;
  background: ${p => p.theme.palette.colors.surfaceElevatedStrong};
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
`

export const ViewButton = styled.button`
  min-width: 84px;
  height: 38px;
  border-radius: 999px;
  border: 0;
  background: ${p => p.active ? p.theme.palette.colors.badgeMuted : 'transparent'};
  color: ${p => p.theme.palette.colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
`

export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  align-items: start;
`

export const ContentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

export const EmptyState = styled.div`
  min-height: 320px;
  border: 1px dashed ${p => p.theme.palette.colors.borderSoft};
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  padding: 32px;
  background: ${p => p.theme.palette.colors.surfaceElevated};
`

export const EmptyTitle = styled.div`
  font-family: Gilroy;
  font-size: 28px;
  font-weight: 700;
  color: ${p => p.theme.palette.colors.text};
`

export const EmptyText = styled.p`
  margin: 0;
  max-width: 360px;
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.theme.palette.colors.muted};
`

export const DraggableCard = styled.div`
  position: relative;
  width: 100%;
  opacity: ${p => p.dragging ? 0.42 : 1};
  transform: ${p => p.dragging ? 'scale(0.98)' : 'none'};
  transition: opacity 0.2s ease, transform 0.2s ease;

  ${p => p.selected ? css`
    & > div {
      border-color: ${p.theme.palette.blue.main};
      box-shadow:
        0 30px 64px ${p.theme.palette.colors.shadowLift},
        inset 0 1px 0 ${p.theme.palette.colors.borderStrong};
    }
  ` : ''}
`

export const CardSelection = styled.label`
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 3;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.palette.colors.surfaceElevatedSoft};
  box-shadow: 0 12px 28px ${p => p.theme.palette.colors.shadowStrong};
  cursor: pointer;
`

export const SelectionInput = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`

export const CardActionBar = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 3;
  display: flex;
  gap: 8px;
`

export const CardActionButton = styled.button`
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.palette.colors.surfaceElevatedSoft};
  box-shadow: 0 12px 28px ${p => p.theme.palette.colors.shadowStrong};
  color: ${p => p.theme.palette.colors.text};
  cursor: pointer;
`

export const ListRow = styled.div`
  position: relative;
  opacity: ${p => p.dragging ? 0.45 : 1};
`

export const ListRowInner = styled.div`
  display: grid;
  grid-template-columns: auto 72px 1fr auto;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 22px;
  background: ${p => p.theme.palette.colors.surfaceElevated};
  box-shadow: 0 18px 42px ${p => p.theme.palette.colors.shadowStrong};
  cursor: pointer;

  @media (max-width: 700px) {
    grid-template-columns: auto 56px 1fr;
  }
`

export const Thumbnail = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 18px;
  object-fit: cover;
  box-shadow: 0 14px 32px ${p => p.theme.palette.colors.shadowStrong};

  @media (max-width: 700px) {
    width: 56px;
    height: 56px;
  }
`

export const ListMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const ListTitle = styled.div`
  font-family: Gilroy;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.theme.palette.colors.text};
`

export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

export const MetaTag = styled.div`
  min-height: 24px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${p => p.theme.palette.colors.badgeMuted};
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.theme.palette.colors.text};
  text-transform: uppercase;
`

export const ListActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 700px) {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
`
